import { ErrorHandler, Injectable, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { SeasonSettingsService } from '@coin/shared/season-mgmt/data-access';
import { GenericSeasonSettings, PositionEvaluationSetting, Season } from '@coin/shared/season-mgmt/util';
import { LoadingService } from '@coin/shared/core/data-access';
import { DisplayedCurrency, SeasonTypeEnum } from '@coin/shared/core/util-enums';
import { ListViewTagFilterParameter, PaginatedResult } from '@coin/shared/core/util-models';
import { produce } from 'immer';
import { Observable, of, Subject } from 'rxjs';
import { FilterSortState } from '@coin/customer/shared/filter-sort-bar-data-access';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { MercerSupportPositionService } from '../../mercer-shared/services/mercer-support-position.service';
import { MercerSupportAddPosition } from '../../mercer-shared/models/mercer-support-add-position.model';
import { MeritSupportSortingParameter } from '../../merit/merit-support/enums/merit-support-sorting-parameter.enum';
import {
  MercerEvaluationMember,
  MercerEvaluationMemberDetails,
  PositionSearchSlim
} from '../../shared/models/mercer-support-evaluation-member.model';
import { MercerSupportService } from '../../shared/services/mercer-support.service';

const STATE_DEFAULTS: MercerSupportStateModel = {
  evaluationMembers: [],
  seasons: [],
  season: undefined,
  filterParameter: [],
  currentPage: 0,
  maxPage: 0,
  sortingParameter: MeritSupportSortingParameter.Default,
  selectedCurrency: DisplayedCurrency.Local,
  seasonSettings: undefined
};

const DEFAULT_PAGE_SIZE = 15;

interface MercerSupportStateModel {
  evaluationMembers: MercerEvaluationMember[];
  seasons: Season[];
  season: Season;
  filterParameter: ListViewTagFilterParameter[];
  currentPage: number;
  maxPage: number;
  sortingParameter: string;
  selectedCurrency: DisplayedCurrency;
  seasonSettings: PositionEvaluationSetting;
}

interface MercerSupportPositionLock {
  evaluationMemberId: string;
  lock: boolean;
}

// Auto-migrated from NGXS
@Injectable({ providedIn: 'root' })
export class MercerSupportState {
  private mercerSupportService = inject(MercerSupportService);
  private readonly filterSortState = inject(FilterSortState);
  private seasonSettingsService = inject(SeasonSettingsService);
  private mercerSupportPositionService = inject(MercerSupportPositionService);
  private loadingService = inject(LoadingService);
  private errorHandler = inject(ErrorHandler);

  public loadMercerSupportEvaluationMembersSuccessful$ = new Subject<void>();
  public setEvaluationMemberBySearchSuccessful$ = new Subject<void>();
  public setMercerSupportPositionLockSuccessful$ = new Subject<MercerSupportPositionLock>();

  private mercerSupportStateModel = signal<MercerSupportStateModel>(STATE_DEFAULTS);

  public evaluationMembers = computed(() => this.mercerSupportStateModel().evaluationMembers);
  public seasons = computed(() => this.mercerSupportStateModel().seasons);
  public season = computed(() => this.mercerSupportStateModel().season);
  public selectedCurrency = computed(() => this.mercerSupportStateModel().selectedCurrency);
  public readonly selectedCurrencyChange$ = toObservable(this.selectedCurrency);
  public filterParameter = computed(() => this.mercerSupportStateModel().filterParameter);
  public seasonSettings = computed(() => this.mercerSupportStateModel().seasonSettings);
  public queryText = computed(() =>
    MercerSupportState.getFilterParameterText(this.mercerSupportStateModel().filterParameter, false)
  );
  public isLastPage = computed(
    () => this.mercerSupportStateModel().currentPage >= this.mercerSupportStateModel().maxPage
  );

  private getSortingParameterText(state: MercerSupportStateModel): string {
    return state.sortingParameter !== MeritSupportSortingParameter.Default
      ? `&Sorting.Property=${state.sortingParameter}`
      : '';
  }

  static getFilterParameterText(filterParameter: ListViewTagFilterParameter[], showQueryPrefix = true): string {
    let filterText = '';

    filterParameter.forEach(param => {
      filterText += `&${showQueryPrefix ? 'Query.' : ''}${encodeURIComponent(param.category)}=${encodeURIComponent(param.value)}`;
    });

    return filterText ?? '';
  }

  mercerSupportAddNewPosition(payload: MercerSupportAddPosition): Observable<unknown> {
    const seasonId = this.mercerSupportStateModel().season.id;

    return this.mercerSupportPositionService.addPosition(seasonId, payload).pipe(
      map(result => {
        this.mercerSupportStateModel.update(currentState =>
          produce(currentState, state => {
            state.evaluationMembers.unshift(result);
          })
        );
      })
    );
  }

  loadMercerSupportEvaluationMembers(): Observable<PaginatedResult<MercerEvaluationMember>> {
    const seasonId = this.mercerSupportStateModel().season.id;
    let filterText = this.filterSortState.getSortAndFilterText();

    filterText +=
      MercerSupportState.getFilterParameterText(this.mercerSupportStateModel().filterParameter) +
      this.getSortingParameterText(this.mercerSupportStateModel());

    return this.mercerSupportService.getEvaluationMembers(seasonId, 0, DEFAULT_PAGE_SIZE, filterText).pipe(
      this.loadingService.withLoadingScreen,
      tap(result => {
        this.mercerSupportStateModel.update(currentState =>
          produce(currentState, state => {
            state.evaluationMembers = result.content;
            state.currentPage = 0;
            state.maxPage = result.pageCount - 1;
          })
        );
      }),
      tap(() => this.loadMercerSupportEvaluationMembersSuccessful$.next())
    );
  }

  lazyLoadMercerSupportEvaluationMembers(): Observable<PaginatedResult<MercerEvaluationMember>> {
    const seasonId = this.mercerSupportStateModel().season.id;
    const { currentPage, maxPage } = this.mercerSupportStateModel();

    if (currentPage >= maxPage) {
      // ngxs compatibility: a handler returning undefined still completed the dispatch
      return of(undefined);
    }
    let filterText = this.filterSortState.getSortAndFilterText();
    filterText +=
      MercerSupportState.getFilterParameterText(this.mercerSupportStateModel().filterParameter) +
      this.getSortingParameterText(this.mercerSupportStateModel());
    return this.mercerSupportService
      .getEvaluationMembers(seasonId, currentPage + 1, DEFAULT_PAGE_SIZE, filterText)
      .pipe(
        this.loadingService.withLoadingScreen,
        tap(result => {
          this.mercerSupportStateModel.update(currentState =>
            produce(currentState, state => {
              const evaluationMembers = [...state.evaluationMembers, ...result.content];

              state.evaluationMembers = evaluationMembers?.filter(
                (evaluationMember, index) => evaluationMembers.findIndex(e => e.id === evaluationMember.id) === index
              ); // delete all duplicates
              state.currentPage += 1;
            })
          );
        })
      );
  }

  setEvaluationMemberBySearch(payload: PositionSearchSlim): Observable<MercerEvaluationMemberDetails> {
    const seasonId = this.mercerSupportStateModel().season.id;
    return this.mercerSupportService.getEvaluationMemberDetails(seasonId, payload.evaluationMemberId).pipe(
      tap(newEvaluationMember => {
        this.mercerSupportStateModel.update(currentState =>
          produce(currentState, state => {
            const evaluationMembersWithoutSearchedEmployee = state.evaluationMembers.filter(
              evaluationMember => evaluationMember.id !== newEvaluationMember.id
            );
            state.evaluationMembers = [newEvaluationMember, ...evaluationMembersWithoutSearchedEmployee];
          })
        );
      }),
      tap(() => this.setEvaluationMemberBySearchSuccessful$.next())
    );
  }

  setMercerSupportEvaluationMember(payload: MercerEvaluationMember): void {
    this.mercerSupportStateModel.update(currentState =>
      produce(currentState, state => {
        const index = state.evaluationMembers?.findIndex(member => payload.id === member.id);
        state.evaluationMembers[index] = payload;
      })
    );
  }

  patchMercerSupportEvaluationMember(id: string, payload: Partial<MercerEvaluationMember>): void {
    this.mercerSupportStateModel.update(currentState =>
      produce(currentState, state => {
        const index = state.evaluationMembers?.findIndex(member => id === member.id);
        Object.assign(state.evaluationMembers[index], payload);
      })
    );
  }

  loadMercerSupportSeasons(partnerEndDateAt?: string): Observable<void> {
    return this.mercerSupportService.getSeasons(partnerEndDateAt).pipe(
      tap(seasons => this.mercerSupportStateModel.update(state => ({ ...state, seasons }))),
      filter(seasons => seasons?.length === 1),
      switchMap(seasons => this.setMercerSupportSeason(seasons[0]))
    );
  }

  setMercerSupportSeason(season: Season): Observable<void> {
    return this.loadMercerSupportSeasonSettings({ id: season.id, type: SeasonTypeEnum.PositionEvaluation }).pipe(
      map(() => undefined),
      tap(() => this.mercerSupportStateModel.update(state => ({ ...state, season })))
    );
  }

  setMercerSupportPositionLock(payload: MercerSupportPositionLock): Observable<unknown> {
    const { season } = this.mercerSupportStateModel();
    return this.mercerSupportService.updateLock(season?.id, payload.evaluationMemberId, payload.lock).pipe(
      map(member => this.setMercerSupportEvaluationMemberDetached(member)),
      tap(() => this.setMercerSupportPositionLockSuccessful$.next(payload))
    );
  }

  promoteMercerSupportMember(evaluationMemberId: string): Observable<unknown> {
    const { season } = this.mercerSupportStateModel();
    return this.mercerSupportService
      .updateEvaluationMemberDetails(season?.id, { id: evaluationMemberId, wasPromoted: true })
      .pipe(map(member => this.setMercerSupportEvaluationMemberDetached(member)));
  }

  undoPromoteMercerSupportMember(evaluationMemberId: string): Observable<unknown> {
    const { season } = this.mercerSupportStateModel();
    return this.mercerSupportService
      .updateEvaluationMemberDetails(season?.id, { id: evaluationMemberId, wasPromoted: false })
      .pipe(map(member => this.setMercerSupportEvaluationMemberDetached(member)));
  }

  // ngxs compatibility: errors of the dispatched action only reached the ErrorHandler and never ended the calling stream
  private setMercerSupportEvaluationMemberDetached(payload: MercerEvaluationMember): void {
    try {
      this.setMercerSupportEvaluationMember(payload);
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }

  resetMercerSupportState(): void {
    this.mercerSupportStateModel.update(state => ({ ...state, ...STATE_DEFAULTS }));
  }

  setMercerSupportCurrency(currency: DisplayedCurrency): void {
    this.mercerSupportStateModel.update(state => ({ ...state, selectedCurrency: currency }));
  }

  addMercerSupportFilterParameter(payload: ListViewTagFilterParameter): void {
    this.mercerSupportStateModel.update(currentState =>
      produce(currentState, state => {
        state.filterParameter.push(payload);
      })
    );
  }

  setMercerSupportFilterParameters(payload: ListViewTagFilterParameter[]): void {
    this.mercerSupportStateModel.update(state => ({ ...state, filterParameter: payload }));
  }

  clearMercerSupportFilterParameterKey(category: string): void {
    this.mercerSupportStateModel.update(currentState =>
      produce(currentState, state => {
        state.filterParameter = state.filterParameter.filter(param => param.category !== category);
      })
    );
  }

  loadMercerSupportSeasonSettings(payload: { id: string; type: SeasonTypeEnum }): Observable<GenericSeasonSettings> {
    return this.seasonSettingsService
      .getSeasonSettings(payload.id, payload.type)
      .pipe(
        tap((seasonSettings: PositionEvaluationSetting) =>
          this.mercerSupportStateModel.update(state => ({ ...state, seasonSettings }))
        )
      );
  }

  changeValidationManager(payload: {
    evaluationMemberId: string;
    newValidationManagerId: string;
    organisationSnapshotId?: string;
  }): Observable<unknown> {
    const { season } = this.mercerSupportStateModel();
    return this.mercerSupportService.changeValidationManager(season.id, payload.evaluationMemberId, payload);
  }
}
